import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { CopyResultButton } from "@/components/CopyResultButton";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function PercentCompositionCalculator() {
  const [elementMass, setElementMass] = useState("");
  const [totalMass, setTotalMass] = useState("");
  const [result, setResult] = useState<number | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Chemistry", url: "https://calclab.app/chemistry" },
      { name: "Percent Composition Calculator", url: "https://calclab.app/calculator/percent-composition" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Percent Composition Calculator",
      "Calculate percentage of each element in a compound. Free online chemistry calculator with instant results and detailed explanations.",
      "https://calclab.app/calculator/percent-composition",
      lang
    )
  ]);

  const calculate = () => {
    const em = parseFloat(elementMass);
    const tm = parseFloat(totalMass);

    if (isNaN(em) || isNaN(tm) || tm <= 0 || em < 0 || em > tm) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid numbers. Element mass must be ≤ total mass.",
        variant: "destructive",
      });
      return;
    }

    const percent = (em / tm) * 100;
    setResult(percent);

    toast({
      title: "Calculation Complete",
      description: `Percent Composition: ${percent.toFixed(2)}%`,
    });
  };

  const reset = () => {
    setElementMass("");
    setTotalMass("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Percent Composition Calculator" 
      calculatorPath="/calculator/percent-composition"
      category="chemistry"
      subcategory="Stoichiometry"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Percent Composition Calculator</CardTitle>
            <CardDescription>
              Calculate the percentage of an element by mass
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="elementMass">Element Mass (g)</Label>
                <Input
                  id="elementMass"
                  type="number"
                  step="any"
                  value={elementMass}
                  onChange={(e) => setElementMass(e.target.value)}
                  placeholder="Enter element mass"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="totalMass">Total Compound Mass (g)</Label>
                <Input
                  id="totalMass"
                  type="number"
                  step="any"
                  value={totalMass}
                  onChange={(e) => setTotalMass(e.target.value)}
                  placeholder="Enter total mass"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result !== null && (
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Result</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-2xl font-bold break-all overflow-wrap-anywhere flex-1">{result.toFixed(2)}%</p>
                    <CopyResultButton value={`${result.toFixed(2)}%`} />
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Formula</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-mono">% = (Element Mass / Total Mass) × 100</p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
